import { Dayjs } from 'dayjs'
import { Account } from '../../../features/account/types/Account'
import { Attachment } from '../../../types/attachment/attachment'
import { Dimension, DimensionQuery } from '../../../features/dimension/types/Dimension'
import { TaskApplication } from '../../superuser/types'

export enum VoucherType {
  JOURNAL = 'JOURNAL',
  AUTO_CONS_MI = 'AUTO_CONS_MI',
  AUTO_CONS_ASSOC = 'AUTO_CONS_ASSOC'
}

export enum MatchClassification {
  // paired AND clean — never combined with other classifications
  VALID = 'VALID', // ex MATCH
  NO_COUNTERPART = 'NO_COUNTERPART', // ex NO_MATCH
  TIMING_DIFFERENCE = 'TIMING_DIFFERENCE',
  SUM_DIFFERENCE = 'SUM_DIFFERENCE',
  // paired, but the sides disagree because they were booked in different currencies
  // (FX rate / rounding) — a discrepancy, but an expected and usually acceptable one
  CURRENCY_DIFFERENCE = 'CURRENCY_DIFFERENCE',
  INVALID_INCOME_STATEMENT_ACCOUNT = 'INVALID_INCOME_STATEMENT_ACCOUNT',
  INVALID_ASSETS_ACCOUNT = 'INVALID_ASSETS_ACCOUNT',
  INVALID_LIABILITIES_ACCOUNT = 'INVALID_LIABILITIES_ACCOUNT',
  // account outside the elimination map whose statement row is unknown — cannot
  // tell which report it belongs to, but the line is still invalid
  INVALID_ACCOUNT = 'INVALID_ACCOUNT',
  // non-IC voucher (e.g. external supplier) booked on an elimination-mapped IC account
  EXTERNAL_ON_IC_ACCOUNT = 'EXTERNAL_ON_IC_ACCOUNT',
  // reference number already consumed by another voucher/pair (credit note re-using the
  // invoice reference, reference recycling) — surfaced instead of silently dropped
  DUPLICATE_REFERENCE = 'DUPLICATE_REFERENCE'
}

export interface Voucher {
  id: number | string
  companyId: string
  guid: number | string
  type: VoucherType
  status: VoucherStatus
  description: string
  date: string
  transactions: Transaction[]
  invoice?: Invoice
  attachments?: Attachment[]
  credit: number
  debit: number
  // Original booking (ledger) currency of the subsidiary that recorded this voucher, and the
  // debit/credit in that currency. `credit`/`debit` above are always in the group's reporting
  // currency. Absent when the voucher was booked in the reporting currency.
  ledgerCurrency?: string
  ledgerDebit?: number
  ledgerCredit?: number
}

export interface Transaction {
  id: number | string
  voucherType: VoucherType
  guid: number | string
  type: string
  accountCode: number
  accountName: string
  debit: number
  credit: number
  date: string
  description: string
  dimensions?: Dimension[]
  classification?: MatchClassification
  // Original booking (ledger) currency amounts for this line. `debit`/`credit` above are in the
  // group's reporting currency; these are the untouched subsidiary-ledger figures. Absent when
  // the voucher was booked in the reporting currency. Currency code is voucher-level
  // (`Voucher.ledgerCurrency`).
  ledgerDebit?: number
  ledgerCredit?: number
}

export interface Invoice {
  id: number | string
  number: number | string
  referenceNumber?: string
  customerId: number | string
  partner?: Partner
  date: string
  paymentDate?: string
  dueDate?: string
  paid: boolean
  value: number
  currency?: string
  rows: InvoiceRow[]
}

export interface InvoiceRow {
  productId: number | string
  productGroupId?: number | string
  customerId?: number | string
  unit?: string
  quantity: number
  unitPrice: number
  value: number
  date?: string
  paymentDate?: string
  dueDate?: string
}
export interface Partner {
  id?: string
  name: string
}

export interface VoucherQueryParams {
  guid?: string
  companyId?: string
  from?: string | Dayjs
  until?: string | Dayjs
  accounts?: VoucherQueryArray<AccountCondition>
  transactionMinValue?: number
  transactionMaxValue?: number
  voucherType?: VoucherQueryArray<string>
  voucherDesc?: string
  transactionDesc?: string[]
  dimension?: DimensionQuery
  invoiceNumber?: number
  invoiceReference?: string[]
  invoicePartnerId?: VoucherQueryArray<string>
  companyIds?: string[]
  sort?: VoucherQuerySort[]
  paginate?: boolean
  page?: number
  pageSize?: number
  groupBy?: 'voucher' | 'transaction'
}

export type VoucherQueryArray<T> = T[]
export interface VoucherQueryRange<T> {
  from: T
  to: T
}

export type AccountCondition = Account['code'] | VoucherQueryRange<Account['code']>

export type DimensionCondition = Dimension['dimensionId']

export interface Condition<T> {
  op: 'and' | 'or' | 'contains' | 'ncontains' | 'eq' | 'neq'
  value?: T
  children?: Condition<T>[]
}

export interface VoucherQuerySort {
  field: string
  direction: VoucherQuerySortDirection
}

export type VoucherQuerySortDirection = 'ASC' | 'DESC'

export interface VoucherQueryResult {
  page: number
  pages: number
  results: number
  vouchers: Voucher[]
}

export interface TransactionQueryResult {
  page: number
  pages: number
  results: number
  vouchers: Transaction[]
}

export type VoucherStatus = {
  [key in TaskApplication]?: string[]
}

export const VoucherStatuses: VoucherStatus = {
  [TaskApplication.PROCOUNTOR]: [
    'EMPTY',
    'UNFINISHED',
    'NOT_SENT',
    'SENT',
    'RECEIVED',
    'PAID',
    'PAYMENT_DENIED',
    'VERIFIED',
    'APPROVED',
    'INVALIDATED',
    'PAYMENT_QUEUED',
    'PARTLY_PAID',
    'PAYMENT_SENT_TO_BANK',
    'MARKED_PAID',
    'STARTED',
    'INVOICED',
    'OVERRIDDEN',
    'DELETED',
    'UNSAVED',
    'PAYMENT_TRANSACTION_REMOVED'
  ],
  [TaskApplication.NETVISOR]: ['valid', 'invalidated', 'deleted']
}
